/**
 * Partner / tracked-link attribution for sign-ups: `?ref=<code>` (a partner's
 * personal link, e.g. `?ref=lee`) plus the standard `utm_*` params Growth's
 * tracked links use. Pure + injectable storage, unit-tested.
 *
 * Flow (no step depends on analytics consent to WORK, only to PERSIST):
 *  1. Boot (`main.tsx`): `captureReferralFromUrl()` reads the params into
 *     memory. Memory survives SPA navigation landing → /sign-up.
 *  2. /sign-up passes the referral to Clerk as `unsafeMetadata.referral`, so it
 *     survives the Google/Apple redirect and email verification (Clerk copies
 *     it onto the new user) without any browser storage.
 *  3. Only with analytics consent (`om_consent`), it is also kept in
 *     localStorage (`om_ref`) for 30 days, so a prospect who signs up days
 *     after clicking still counts. First partner code wins; listed on /cookies.
 *  4. `ReferralClaimer` (App.tsx), once signed in, POSTs it to
 *     /api/me/referral (server keeps it write-once, accounts < 24h old only),
 *     then forgets the local copy.
 */

export const REFERRAL_STORAGE_KEY = "om_ref";
export const REFERRAL_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
export const REFERRAL_UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;
const CODE_PATTERN = /^[a-z0-9][a-z0-9_-]{1,31}$/;
const UTM_MAX = 100;

export type ReferralUtm = Partial<Record<(typeof REFERRAL_UTM_KEYS)[number], string>>;
export interface Referral {
  code: string | null;
  utm: ReferralUtm | null;
}
export interface ReferralStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export function normalizeCode(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const code = value.trim().toLowerCase();
  return CODE_PATTERN.test(code) ? code : null;
}

function normalizeUtm(value: unknown): ReferralUtm | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const out: ReferralUtm = {};
  for (const key of REFERRAL_UTM_KEYS) {
    const raw = (value as Record<string, unknown>)[key];
    if (typeof raw !== "string") continue;
    const v = Array.from(raw.trim()).slice(0, UTM_MAX).join("");
    if (v) out[key] = v;
  }
  return Object.keys(out).length ? out : null;
}

export function sanitizeReferral(value: unknown): Referral | null {
  if (!value || typeof value !== "object") return null;
  const v = value as Record<string, unknown>;
  const code = normalizeCode(v["code"]);
  const utm = normalizeUtm(v["utm"]);
  return code || utm ? { code, utm } : null;
}

/** `?ref=` (also `?referral=`) and `utm_*`, case-insensitive keys; null when absent. */
export function parseReferral(search: string | null | undefined): Referral | null {
  try {
    const params = new URLSearchParams((search ?? "").replace(/^\?/, ""));
    let code: string | null = null;
    const utm: Record<string, string> = {};
    for (const [rawKey, value] of params) {
      const key = rawKey.trim().toLowerCase();
      if ((key === "ref" || key === "referral") && code === null) code = normalizeCode(value);
      else if ((REFERRAL_UTM_KEYS as readonly string[]).includes(key) && !(key in utm)) utm[key] = value;
    }
    return sanitizeReferral({ code, utm });
  } catch {
    return null;
  }
}

export function readStoredReferral(
  storage: ReferralStorage,
  now: number = Date.now(),
): Referral | null {
  try {
    const raw = storage.getItem(REFERRAL_STORAGE_KEY);
    if (!raw) return null;
    const rec = JSON.parse(raw) as { v?: unknown; at?: unknown; referral?: unknown };
    if (rec.v !== 1 || typeof rec.at !== "number" || now - rec.at > REFERRAL_MAX_AGE_MS) {
      storage.removeItem(REFERRAL_STORAGE_KEY);
      return null;
    }
    return sanitizeReferral(rec.referral);
  } catch {
    return null;
  }
}

/**
 * Keep `incoming` unless a stored record already holds a partner CODE (first
 * code wins for 30 days; a utm-only record is replaced by a later code).
 * Returns true when written.
 */
export function storeReferral(
  storage: ReferralStorage,
  incoming: Referral | null,
  now: number = Date.now(),
): boolean {
  if (!incoming) return false;
  const existing = readStoredReferral(storage, now);
  if (existing?.code) return false;
  if (existing && !incoming.code) return false;
  try {
    storage.setItem(REFERRAL_STORAGE_KEY, JSON.stringify({ v: 1, at: now, referral: incoming }));
    return true;
  } catch {
    return false;
  }
}

export function forgetReferral(storage: ReferralStorage): void {
  try {
    storage.removeItem(REFERRAL_STORAGE_KEY);
  } catch {
    // nothing to do
  }
}

/** Which referral to use: a partner code beats utm-only; this visit beats storage on a tie. */
export function pickReferral(...candidates: Array<Referral | null | undefined>): Referral | null {
  const valid = candidates.map((c) => sanitizeReferral(c)).filter((c): c is Referral => c !== null);
  return valid.find((c) => c.code) ?? valid[0] ?? null;
}

// --- current page load (module memory) -------------------------------------

let currentVisit: Referral | null = null;

export function captureReferral(search: string | null | undefined): Referral | null {
  currentVisit = parseReferral(search);
  return currentVisit;
}
export function getCapturedReferral(): Referral | null {
  return currentVisit;
}
export function resetCapturedReferral(): void {
  currentVisit = null;
}
